import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ProjectAssignmentsApi from "../../../../../infrastructure/api/projectAssignments";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";
import { toJalaliFullDate } from "../../../../middleware/middleware";
import SetVisitDate from "../site-visit/SetVisitDate";
import SupervisorWorkspaceShell from "../SupervisorWorkspaceShell";
import useSupervisorLocationFilters, {
  SupervisorLocationFilterBar,
} from "../locationFilters";
import AssignmentCaseInfoDialog from "./AssignmentCaseInfoDialog";

const ACCEPTED_STATUS = 2;
const COMPLETED_STATUS = 5;

const ASSIGNMENT_STATUS_BY_NAME = {
  Assigned: 1,
  Pending: 1,
  Accepted: 2,
  Rejected: 3,
  Canceled: 4,
  Completed: 5,
  Superseded: 6,
};

const resolveAssignmentStatusValue = (status) => {
  if (status == null || status === "") return null;
  if (typeof status === "number") return status;
  if (typeof status === "string") {
    if (ASSIGNMENT_STATUS_BY_NAME[status] != null) {
      return ASSIGNMENT_STATUS_BY_NAME[status];
    }
    const parsed = Number(status);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const isSiteVisitStatusPending = (status) => {
  if (status == null || status === "") return false;
  if (typeof status === "number") return status === 0;
  const normalized = String(status).trim().toLowerCase();
  return (
    normalized === "pending" ||
    normalized === "0" ||
    normalized === "درحال بررسی" ||
    normalized === "در حال بررسی"
  );
};

const isSiteVisitStatusWithdrawn = (status) => {
  if (status == null || status === "") return false;
  if (typeof status === "number") return status === 4;
  const normalized = String(status).trim().toLowerCase();
  return (
    normalized === "withdrawn" ||
    normalized === "4" ||
    normalized.includes("انصراف")
  );
};

const canShowAssignmentActions = (item) => {
  if (item?.canAccept != null || item?.CanAccept != null) {
    return !!(item?.canAccept ?? item?.CanAccept);
  }
  return resolveAssignmentStatusValue(item?.projectAssignmentStatus) === 1;
};

const statusColorByValue = (status) => {
  const value = resolveAssignmentStatusValue(status);
  if (value === 2) return "success";
  if (value === 3) return "error";
  if (value === 5) return "success";
  if (value === 4 || value === 6) return "gray";
  return "warning";
};

const hasValidVisitDate = (value) => {
  if (!value) return false;
  if (typeof value === "string" && value.startsWith("0001-01-01")) return false;
  if (value instanceof Date && value.getFullYear() <= 1) return false;
  return true;
};

const getVisitMeta = (item) => {
  const statusValue = resolveAssignmentStatusValue(item?.projectAssignmentStatus);
  const scheduledVisitTime =
    item?.relatedSiteVisitScheduledVisitTime ??
    item?.RelatedSiteVisitScheduledVisitTime ??
    null;
  const actualVisitTime =
    item?.relatedSiteVisitActualVisitTime ??
    item?.RelatedSiteVisitActualVisitTime ??
    null;
  const relatedSiteVisitStatus =
    item?.relatedSiteVisitStatus ?? item?.RelatedSiteVisitStatus ?? null;
  const isVisitScheduled = hasValidVisitDate(scheduledVisitTime);
  const isVisitCompleted = hasValidVisitDate(actualVisitTime);
  const isSiteVisitPending = isSiteVisitStatusPending(relatedSiteVisitStatus);
  const isSiteVisitWithdrawn = isSiteVisitStatusWithdrawn(relatedSiteVisitStatus);
  const hasSiteVisit = !!(item?.relatedSiteVisitId || item?.RelatedSiteVisitId);
  const relatedSiteVisitId =
    item?.relatedSiteVisitId || item?.RelatedSiteVisitId || null;
  return {
    statusValue,
    scheduledVisitTime,
    isVisitScheduled,
    isVisitCompleted,
    isSiteVisitPending,
    isSiteVisitWithdrawn,
    relatedSiteVisitStatus,
    hasSiteVisit,
    relatedSiteVisitId,
    showActions: canShowAssignmentActions(item),
    canSchedule:
      (statusValue === ACCEPTED_STATUS ||
        (statusValue === COMPLETED_STATUS && isSiteVisitPending)) &&
      hasSiteVisit &&
      !isVisitCompleted &&
      !isSiteVisitWithdrawn,
    // Allow opening form after accept even before scheduling (e.g. ثبت انصراف هنگام هماهنگی)
    canCompleteForm:
      (statusValue === ACCEPTED_STATUS ||
        (statusValue === COMPLETED_STATUS && isSiteVisitPending)) &&
      hasSiteVisit &&
      (!isVisitCompleted || isSiteVisitPending) &&
      !isSiteVisitWithdrawn,
    mobile: item?.applicantMobile || item?.ApplicantMobile || "",
    referenceId:
      item?.requestReferenceId ||
      item?.RequestReferenceId ||
      item?.requestId ||
      "-",
    fullName:
      [
        item?.applicantFirstName || item?.ApplicantFirstName || "",
        item?.applicantLastName || item?.ApplicantLastName || "",
      ]
        .join(" ")
        .trim() || "—",
    city: item?.cityName || item?.CityName || "—",
    province: item?.provinceName || item?.ProvinceName || "—",
    cityId: item?.cityId ?? item?.CityId ?? null,
    provinceId: item?.provinceId ?? item?.ProvinceId ?? null,
  };
};

const isCurrentAssignment = (item) => {
  const meta = getVisitMeta(item);
  if (meta.statusValue === 1) return true;
  // Accepted and still open, or outcome reverted to «در حال بررسی»
  if (meta.statusValue === ACCEPTED_STATUS) {
    return !meta.isVisitCompleted || meta.isSiteVisitPending;
  }
  // Completed assignment with Pending visit (before reopen) still belongs in current
  if (meta.statusValue === COMPLETED_STATUS && meta.isSiteVisitPending) {
    return true;
  }
  return false;
};

const isClosedAssignment = (item) => !isCurrentAssignment(item);

const resolveListMode = (pathname) => {
  const path = (pathname || "").toLowerCase();
  return path.includes("/closed") ? "closed" : "current";
};

const buildTableRow = (item) => {
  const meta = getVisitMeta(item);
  let visitStatusTitle = "-";
  let visitStatusColor = "gray";
  if (meta.hasSiteVisit) {
    if (meta.isSiteVisitWithdrawn) {
      visitStatusTitle = "انصراف متقاضی";
      visitStatusColor = "error";
    } else if (meta.isSiteVisitPending) {
      visitStatusTitle = "در حال بررسی";
      visitStatusColor = "warning";
    } else if (meta.isVisitCompleted) {
      visitStatusTitle = "تکمیل شد";
      visitStatusColor = "success";
    } else if (meta.isVisitScheduled) {
      visitStatusTitle = "برنامه‌ریزی‌شده";
      visitStatusColor = "primary";
    } else {
      visitStatusTitle = "در انتظار زمان";
      visitStatusColor = "warning";
    }
  } else if (meta.statusValue === ACCEPTED_STATUS) {
    visitStatusTitle = "آماده‌سازی";
    visitStatusColor = "warning";
  }

  return {
    ...item,
    id: item.id ?? item.Id,
    relatedSiteVisitId: meta.relatedSiteVisitId,
    referenceId: String(meta.referenceId),
    applicantFullName: meta.fullName,
    applicantMobileDisplay: meta.mobile || "-",
    locationDisplay: `${meta.province} / ${meta.city}`,
    provinceId: meta.provinceId,
    cityId: meta.cityId,
    provinceName: meta.province === "—" ? "" : meta.province,
    cityName: meta.city === "—" ? "" : meta.city,
    typeTitle: item.projectAssignmentTypeTitle || item.ProjectAssignmentTypeTitle || "-",
    statusTitle:
      item.projectAssignmentStatusTitle ||
      item.ProjectAssignmentStatusTitle ||
      "-",
    statusColor: statusColorByValue(item.projectAssignmentStatus),
    visitStatusTitle,
    visitStatusColor,
    scheduledVisitTimeDisplay: meta.isVisitScheduled
      ? toJalaliFullDate(meta.scheduledVisitTime)
      : "—",
    assignedAtDisplay: item.assignedAt || item.AssignedAt || null,
    _meta: meta,
  };
};

const headCells = [
  { id: "typeTitle", label: "نوع ارجاع" },
  { id: "referenceId", label: "شماره پیگیری" },
  { id: "applicantFullName", label: "متقاضی" },
  { id: "applicantMobileDisplay", label: "موبایل" },
  { id: "locationDisplay", label: "محل" },
  {
    id: "statusTitle",
    label: "وضعیت",
    type: "chip",
    model: { colorField: "statusColor" },
  },
  {
    id: "visitStatusTitle",
    label: "بازدید اولیه",
    type: "chip",
    model: { colorField: "visitStatusColor" },
  },
  {
    id: "scheduledVisitTimeDisplay",
    label: "زمان بازدید",
  },
  {
    id: "assignedAtDisplay",
    label: "تاریخ ارجاع",
    type: "persiandatetime",
  },
];

const SupervisorProjectAssignmentsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const listMode = resolveListMode(location.pathname);
  const isClosed = listMode === "closed";
  const {
    provinceFilter,
    cityFilter,
    provinces,
    filteredCities,
    setProvinceFilter,
    setCityFilter,
    matchesLocation,
  } = useSupervisorLocationFilters();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [caseInfoItem, setCaseInfoItem] = useState(null);
  const [scheduleDialog, setScheduleDialog] = useState({
    open: false,
    siteVisitId: null,
  });
  const [rejectDialog, setRejectDialog] = useState({
    open: false,
    assignmentId: 0,
    reason: "",
  });

  const loadAssignments = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    setError("");
    const res = await ProjectAssignmentsApi.getMyAssignments("Supervisor");
    if (!res.ok) {
      setError(res.message || "خطا در دریافت ارجاع‌ها");
      if (!silent) {
        setAssignments([]);
      }
    } else {
      setAssignments(Array.isArray(res.data) ? res.data : []);
    }
    if (!silent) {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssignments();
  }, []);

  const mergeAssignment = (updated) => {
    if (!updated?.id && !updated?.Id) return false;
    const id = updated.id ?? updated.Id;
    setAssignments((prev) => {
      const idx = prev.findIndex((x) => x.id === id || x.Id === id);
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], ...updated };
      return next;
    });
    return true;
  };

  const handleAccept = async (assignmentId) => {
    setSubmitting(true);
    setError("");
    setSuccess("");
    const res = await ProjectAssignmentsApi.acceptAssignment(
      "Supervisor",
      assignmentId
    );
    if (!res.ok) {
      setError(res.message || "پذیرش ارجاع با خطا مواجه شد");
    } else {
      if (res?.data?.relatedSiteVisitId) {
        setSuccess("ارجاع پذیرفته شد و بازدید اولیه آماده است.");
      } else {
        setSuccess(res.message || "ارجاع با موفقیت پذیرفته شد.");
      }
      if (!mergeAssignment(res.data)) {
        await loadAssignments({ silent: true });
      } else {
        loadAssignments({ silent: true });
      }
    }
    setSubmitting(false);
  };

  const handleReject = async () => {
    if (!rejectDialog.reason?.trim()) {
      setError("ثبت دلیل رد الزامی است.");
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess("");
    const res = await ProjectAssignmentsApi.rejectAssignment(
      "Supervisor",
      rejectDialog.assignmentId,
      rejectDialog.reason
    );

    if (!res.ok) {
      setError(res.message || "رد ارجاع با خطا مواجه شد");
    } else {
      setSuccess(res.message || "ارجاع با موفقیت رد شد.");
      setRejectDialog({ open: false, assignmentId: 0, reason: "" });
      if (!mergeAssignment(res.data)) {
        await loadAssignments({ silent: true });
      } else {
        loadAssignments({ silent: true });
      }
    }
    setSubmitting(false);
  };

  const rows = useMemo(() => {
    const filtered = assignments.filter((item) =>
      isClosed ? isClosedAssignment(item) : isCurrentAssignment(item)
    );
    return filtered.map(buildTableRow).filter(matchesLocation);
  }, [assignments, isClosed, matchesLocation]);

  const tableActions = useMemo(() => {
    const actions = [
      {
        icon: <VisibilityIcon fontSize="small" />,
        color: "primary",
        tooltip: "اطلاعات پرونده",
        onClick: (row) => setCaseInfoItem(row),
      },
    ];

    if (isClosed) {
      actions.push({
        icon: <FactCheckIcon fontSize="small" />,
        color: "primary",
        tooltip: "ویرایش فرم بازدید",
        hidden: (row) => !row.relatedSiteVisitId,
        onClick: (row) =>
          navigate(`/SLN/Supervisor/SiteVisit/${row.relatedSiteVisitId}`, {
            state: { from: location.pathname },
          }),
      });
      return actions;
    }

    actions.push(
      {
        icon: <CheckCircleIcon fontSize="small" />,
        color: "success",
        tooltip: "قبول ارجاع",
        hidden: (row) => !row._meta?.showActions || submitting,
        onClick: (row) => handleAccept(row.id),
      },
      {
        icon: <CancelIcon fontSize="small" />,
        color: "error",
        tooltip: "رد ارجاع",
        hidden: (row) => !row._meta?.showActions || submitting,
        onClick: (row) =>
          setRejectDialog({ open: true, assignmentId: row.id, reason: "" }),
      },
      {
        icon: <EventAvailableIcon fontSize="small" />,
        color: "primary",
        tooltip: "ثبت زمان بازدید",
        hidden: (row) => !row._meta?.canSchedule || submitting,
        onClick: (row) =>
          setScheduleDialog({
            open: true,
            siteVisitId: row.relatedSiteVisitId,
          }),
      },
      {
        icon: <FactCheckIcon fontSize="small" />,
        color: "primary",
        tooltip: "تکمیل فرم بازدید / ثبت انصراف",
        hidden: (row) => !row._meta?.canCompleteForm || submitting,
        onClick: (row) =>
          navigate(`/SLN/Supervisor/SiteVisit/${row.relatedSiteVisitId}`, {
            state: { from: location.pathname },
          }),
      }
    );

    return actions;
  }, [handleAccept, isClosed, location.pathname, navigate, submitting]);

  return (
    <SupervisorWorkspaceShell
      title={isClosed ? "پروژه‌های قبلی" : "پروژه‌های جاری"}
      actions={
        <IconButton
          onClick={() => loadAssignments()}
          disabled={loading || submitting}
          aria-label="بروزرسانی"
        >
          <RefreshIcon />
        </IconButton>
      }
    >
      <BreadcrumbsComponent />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {isClosed
          ? "پروژه‌های تکمیل‌شده، ردشده یا بسته‌شده را اینجا ببینید. ناظر ارجاع‌شده می‌تواند فرم بازدید ثبت‌شده را ویرایش کند."
          : "برای هماهنگی زمان بازدید: آیکن تقویم در ردیف، یا از «اطلاعات پرونده» دکمه «ثبت زمان بازدید» را بزنید."}
      </Typography>

      <SupervisorLocationFilterBar
        provinceFilter={provinceFilter}
        cityFilter={cityFilter}
        provinces={provinces}
        filteredCities={filteredCities}
        onProvinceChange={setProvinceFilter}
        onCityChange={setCityFilter}
      />

      <Stack spacing={2}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {success ? (
          <Alert
            severity="success"
            onClose={() => setSuccess("")}
            sx={{
              position: { xs: "sticky", md: "static" },
              top: { xs: 48, md: "auto" },
              zIndex: 1,
            }}
          >
            {success}
          </Alert>
        ) : null}

        <SetVisitDate
          open={scheduleDialog.open}
          id={scheduleDialog.siteVisitId}
          onClose={(loadAgain) => {
            setScheduleDialog({ open: false, siteVisitId: null });
            if (loadAgain) {
              // Keep page scroll position after scheduling.
              loadAssignments({ silent: true });
            }
          }}
        />

        {!loading && rows.length === 0 ? (
          <Alert severity="info">
            {provinceFilter || cityFilter
              ? "با فیلتر استان/شهر انتخاب‌شده موردی یافت نشد."
              : isClosed
                ? "پروژه قبلی یا بسته‌شده‌ای ثبت نشده است."
                : "ارجاع فعالی برای شما ثبت نشده است."}
          </Alert>
        ) : (
          <EnhancedTable
            rowKey="id"
            rows={rows}
            headCells={headCells}
            loading={loading}
            defaultSort={{ orderBy: "assignedAtDisplay", order: "desc" }}
            pageSize={25}
            tableMinWidth={980}
            actionsMode={isMobile ? "menu" : "inline"}
            actions={tableActions}
          />
        )}
      </Stack>

      <AssignmentCaseInfoDialog
        open={Boolean(caseInfoItem)}
        item={caseInfoItem}
        canSchedule={Boolean(caseInfoItem?._meta?.canSchedule)}
        onScheduleVisit={() => {
          const siteVisitId = caseInfoItem?._meta?.relatedSiteVisitId;
          if (!siteVisitId) return;
          setCaseInfoItem(null);
          setScheduleDialog({ open: true, siteVisitId });
        }}
        onClose={() => setCaseInfoItem(null)}
      />

      <Dialog
        open={rejectDialog.open}
        onClose={() =>
          setRejectDialog({ open: false, assignmentId: 0, reason: "" })
        }
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        disableScrollLock
      >
        <DialogTitle>رد ارجاع بازدید</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            margin="dense"
            label="دلیل رد"
            multiline
            minRows={3}
            value={rejectDialog.reason}
            onChange={(e) =>
              setRejectDialog((prev) => ({ ...prev, reason: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() =>
              setRejectDialog({ open: false, assignmentId: 0, reason: "" })
            }
            disabled={submitting}
          >
            انصراف
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleReject}
            disabled={submitting}
          >
            ثبت رد
          </Button>
        </DialogActions>
      </Dialog>
    </SupervisorWorkspaceShell>
  );
};

export default SupervisorProjectAssignmentsPage;
